import { useState, useEffect, useRef } from 'react'
import { useApiStatus } from '@/hooks/use-api-status'

interface SessionCountdown {
  /** Human-readable remaining time, e.g. "6d 23h" or "45m" */
  remainingLabel: string | null
  /** Whether the offline grace period has expired */
  expired: boolean
  /** Whether less than 1 hour remains */
  nearExpiry: boolean
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'Expired'
  const totalSeconds = Math.floor(ms / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

export function useSessionCountdown(): SessionCountdown {
  const { connected, offlineDeadline } = useApiStatus()
  const [now, setNow] = useState(Date.now())
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!connected && offlineDeadline) {
      timerRef.current = setInterval(() => setNow(Date.now()), 60_000)
      return () => {
        if (timerRef.current) clearInterval(timerRef.current)
      }
    }
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [connected, offlineDeadline])

  if (!offlineDeadline || connected) {
    return { remainingLabel: null, expired: false, nearExpiry: false }
  }

  const remaining = offlineDeadline - now
  return {
    remainingLabel: formatRemaining(Math.max(0, remaining)),
    expired: remaining <= 0,
    nearExpiry: remaining > 0 && remaining < 3600_000,
  }
}
